import { Card, CardContent, CardHeader, CardTitle, Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@city-sage/aurum-avis-labs-components';
import { useTranslation } from 'react-i18next';

const HelpPage = () => {
  const { t } = useTranslation();

  return (
    <div className="container mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-8">{t('pages.help.title')}</h1>
      
      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('pages.help.gettingStarted.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              {t('pages.help.gettingStarted.description')}
            </p>
            <Accordion type="multiple" collapsible className="w-full">
              <AccordionItem value="create-account">
                <AccordionTrigger>
                  {t('pages.help.gettingStarted.faq.createAccount.question')}
                </AccordionTrigger>
                <AccordionContent>
                  {t('pages.help.gettingStarted.faq.createAccount.answer')}
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="who-for">
                <AccordionTrigger>
                  Who is CitySage for?
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Travelers planning smarter trips</li>
                    <li>Creators and influencers looking to share or monetize guides</li>
                    <li>Tour guides who want to offer self-guided or virtual experiences</li>
                    <li>Anyone who loves exploring through stories, not spreadsheets</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="share-tours">
                <AccordionTrigger>
                  Can I share my tours with others?
                </AccordionTrigger>
                <AccordionContent>
                  Yes — every tour comes with a unique link. You can send it to friends, share it on socials, or even embed it in your blog or bio.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="monetize-tours">
                <AccordionTrigger>
                  Can I monetize my tours?
                </AccordionTrigger>
                <AccordionContent>
                  Yes. Add affiliate links, booking tools, or premium content to your tours. More creator tools are coming soon.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="download">
                <AccordionTrigger>
                  Do I need to download anything?
                </AccordionTrigger>
                <AccordionContent>
                  No — CitySage runs in your browser on mobile and desktop. Nothing to install.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="difference">
                <AccordionTrigger>
                  What makes CitySage different from other travel apps?
                </AccordionTrigger>
                <AccordionContent>
                  It's built like a social platform — not a spreadsheet. Tours feel like Instagram stories with maps. You can plan smarter, share better, and follow other travelers' journeys around the world.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>AI & Credits</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="multiple" collapsible className="w-full">
              <AccordionItem value="ai-routing">
                <AccordionTrigger>
                  How does AI route creation work?
                </AccordionTrigger>
                <AccordionContent>
                  Just tell CitySage where you're going and what you love — our AI will generate a full itinerary with personalized stops and descriptions in seconds.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="ai-cost">
                <AccordionTrigger>
                  Does it cost anything to use AI tour creation?
                </AccordionTrigger>
                <AccordionContent>
                  Each AI-generated stop costs 1 credit. So if your tour has 5 stops, it will use 5 credits.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="stop-definition">
                <AccordionTrigger>
                  What's included in a "stop"?
                </AccordionTrigger>
                <AccordionContent>
                  A stop is any individual location on your tour — like a museum, café, viewpoint, or neighborhood. It includes a name, description, and map pin.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="free-credits">
                <AccordionTrigger>
                  Do I get free credits?
                </AccordionTrigger>
                <AccordionContent>
                  Yes — every new user gets 100 free credits to start building and exploring right away. No payment needed.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="get-credits">
                <AccordionTrigger>
                  How can I get more credits?
                </AccordionTrigger>
                <AccordionContent>
                  You can purchase additional credits whenever you want. There's no subscription — just top up as needed.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="manual-tours">
                <AccordionTrigger>
                  Can I build tours without using credits?
                </AccordionTrigger>
                <AccordionContent>
                  Yes! You can manually create, edit, and share routes for free. Credits are only used when generating stops with AI.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('pages.help.accountSecurity.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="reset-password">
                <AccordionTrigger>
                  {t('pages.help.accountSecurity.faq.resetPassword.question')}
                </AccordionTrigger>
                <AccordionContent>
                  {t('pages.help.accountSecurity.faq.resetPassword.answer')}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('pages.help.contactSupport.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              {t('pages.help.contactSupport.description')}
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Email: <a href="mailto:citysage@aurum-avis-labs.ch">citysage@aurum-avis-labs.ch</a></li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HelpPage; 